import { all, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { DISABLE_HLA_RESULT_REQUEST, DISABLE_HLA_RESULT_SUCCESS, HLA_RESULT_INACTIVATION_NAMESPACE, DISABLE_HLA_RESULT_FAILURE, ENABLE_HLA_RESULT_REQUEST, ENABLE_HLA_RESULT_SUCCESS, ENABLE_HLA_RESULT_FAILURE } from '../actions/hla-result-inactivation';
import { HLAResultInactivation, HLAResultRef } from "../types/shared/v2/HLAResult";
import { RootState } from "../reducers/root";
import { selectPatientAndClassHLAResultInactivationObjects } from "../selectors/hla-result-inactivation";
import { PrivacyDomain, StoreObject } from "../util/objects";
import { CREATE_OBJECT_FAILURE, CREATE_OBJECT_SUCCESS, createObject, DELETE_OBJECT_FAILURE, DELETE_OBJECT_SUCCESS, deleteObject, FETCH_OBJECT_FAILURE, FETCH_OBJECT_SUCCESS, UPDATE_OBJECT_FAILURE, UPDATE_OBJECT_SUCCESS, updateObject } from '../actions/objects';
import { runPatientRuleEngine } from "./hlaEngineVariable";


import { areHLAResultRefEquals } from "../util/hla-result";
export function* hlaResultSaga(): Generator<any, any, any> {
  yield all([
    takeEvery(DISABLE_HLA_RESULT_REQUEST, disableHLAResult),
    takeEvery(ENABLE_HLA_RESULT_REQUEST, enableHLAResult),
    takeLatest(action => action.type === DISABLE_HLA_RESULT_SUCCESS || action.type === ENABLE_HLA_RESULT_SUCCESS, runPatientRuleEngineFromInlogId)
  ]);
}

export function* disableHLAResult(
  action
): Generator<any, any, any> {
  const inlogId: string = action.inlogId;
  const hlaResult: HLAResultRef = action.hlaResult;

  const hlaResultInactivations: StoreObject<HLAResultInactivation>[] = yield select((state: RootState) => selectPatientAndClassHLAResultInactivationObjects(state, inlogId, hlaResult.class))

  const match = hlaResultInactivations.find(inactivation => {
    return areHLAResultRefEquals(hlaResult, inactivation.data.ref.hlaResult)
  })

  let result: { success?: any, failure?: any } = {};

  try {
    if (!match) {
      yield put(createObject<HLAResultInactivation>(HLA_RESULT_INACTIVATION_NAMESPACE, PrivacyDomain.Public, { ref: { hlaResult, patient: { inlogId } }, disabled: true }));

      result = yield race({
        success: take((action) => action.type === CREATE_OBJECT_SUCCESS && action.namespace === HLA_RESULT_INACTIVATION_NAMESPACE),
        failure: take((action) => action.type === CREATE_OBJECT_FAILURE && action.namespace === HLA_RESULT_INACTIVATION_NAMESPACE),
      });
    } else {
      yield put(updateObject<HLAResultInactivation>(HLA_RESULT_INACTIVATION_NAMESPACE, PrivacyDomain.Public, match.id, { disabled: true }, true));

      result = yield race({
        success: take((action) => action.type === UPDATE_OBJECT_SUCCESS && action.namespace === HLA_RESULT_INACTIVATION_NAMESPACE),
        failure: take((action) => action.type === UPDATE_OBJECT_FAILURE && action.namespace === HLA_RESULT_INACTIVATION_NAMESPACE),
      });
    }
  } catch (err) {
    yield put({ type: DISABLE_HLA_RESULT_FAILURE, txId: (action as any).txId, hlaResult, inlogId, error: err });
    return
  }

  if (result.success) {
    yield put({ type: DISABLE_HLA_RESULT_SUCCESS, txId: (action as any).txId, hlaResult, inlogId })
  } else {
    yield put({ type: DISABLE_HLA_RESULT_FAILURE, txId: (action as any).txId, hlaResult, inlogId, error: result.failure?.error ?? new Error(`Could not disable hla result '${JSON.stringify(hlaResult)}' for patient '${inlogId}' !`) });
  }
}

export function* enableHLAResult(
  action
): Generator<any, any, any> {
  const inlogId: string = action.inlogId;
  const hlaResult: HLAResultRef = action.hlaResult;

  const hlaResultInactivations: StoreObject<HLAResultInactivation>[] = yield select((state: RootState) => selectPatientAndClassHLAResultInactivationObjects(state, inlogId, hlaResult.class))

  const match = hlaResultInactivations.find(inactivation => {
    return areHLAResultRefEquals(hlaResult, inactivation.data.ref.hlaResult)
  })

  if (!match) {
    yield put({ type: ENABLE_HLA_RESULT_SUCCESS, txId: (action as any).txId, hlaResult, inlogId })
    return
  }

  let result: { success?: any, failure?: any } = {};

  try {
    if (match) {
      yield put(deleteObject(HLA_RESULT_INACTIVATION_NAMESPACE, PrivacyDomain.Public, match.id));

      result = yield race({
        success: take((action) => action.type === DELETE_OBJECT_SUCCESS && action.namespace === HLA_RESULT_INACTIVATION_NAMESPACE),
        failure: take((action) => action.type === DELETE_OBJECT_FAILURE && action.namespace === HLA_RESULT_INACTIVATION_NAMESPACE),
      });
    }
  } catch (err) {
    yield put({ type: ENABLE_HLA_RESULT_FAILURE, txId: (action as any).txId, hlaResult, inlogId, error: err });
    return
  }

  if (result.success) {
    yield put({ type: ENABLE_HLA_RESULT_SUCCESS, txId: (action as any).txId, hlaResult, inlogId })
    return
  } else {
    yield put({ type: ENABLE_HLA_RESULT_FAILURE, txId: (action as any).txId, hlaResult, inlogId, error: result.failure?.error ?? new Error(`Could not enable hla result '${JSON.stringify(hlaResult)}' for patient '${inlogId}' !`) });
    return
  }
}

export function* runPatientRuleEngineFromInlogId(
  action
): Generator<any, any, any> {
  const inlogId = action.inlogId;
  if (!inlogId) return;

  yield runPatientRuleEngine(inlogId);
}